// React, Next
import { useTranslation } from 'next-i18next'
import { useContext, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image, { StaticImageData } from 'next/image'
import { useRouter } from 'next/router'

// MUI
import { motion } from 'framer-motion'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'
import { translateHref } from 'assets/functions/links'
import PhotoCarrousel from 'components/carrousel/PhotoCarrousel'

// Config
import i18nextConfig from 'next-i18next.config'

// Images - one per slide
import SlideSecurity from 'public/img/services/shop_floor.webp'
import SlideConnectivity from 'public/img/technology/kroemtech_technology.webp'
import SlideMonitoring from 'public/img/products/Air1000/air-1000.webp'

// Style
import themeColors from '@/assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import classes from './HeroSection2-CSS'
import { getFontH1, getFontH2 } from '@/assets/theme/fonts'

export default function HeroSection2() {
  const { t } = useTranslation('index')
  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale

  const Ref: any = useRef(null)

  const { screenDimensions } = useContext(GlobalContext)
  const [mobileMode, setMobileMode] = useState(false)

  const [animationTimer, setAnimationTimer] = useState(80)
  const [brightness, setBrightness] = useState(100)

  const [currentSlide, setCurrentSlide] = useState(0)
  const [slideDelay, setSlideDelay] = useState(3000)

  // Slow drift across the still photo, so the hero never sits completely flat.
  const [viewPointW, setViewPointW] = useState(0)
  const [viewPointWDir, setViewPointWDir] = useState(false) // true = right to left

  useEffect(() => {
    if (!animationTimer) return

    const intervalId = setInterval(() => {
      setAnimationTimer(animationTimer - 1)
      if (animationTimer <= 60 && brightness > 50) setBrightness(brightness - 1)
    }, 50)

    return () => clearInterval(intervalId)
  }, [animationTimer, brightness])

  useEffect(() => {
    setMobileMode(screenDimensions.width <= breakpoints.desktopToMobile)
  }, [screenDimensions])

  const getPadding = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopMid)
      return `0 0 0 ${screenDimensions.width / 100}rem`
    if (w > breakpoints.desktopSmall)
      return `0 0 0 ${screenDimensions.width / 120}rem`
    if (w > breakpoints.desktopToMobile)
      return `0 0 0 ${screenDimensions.width / 200}rem`
    return '0 0.75rem 0 0.75rem'
  }

  useEffect(() => {
    setTimeout(() => setSlideDelay(1000), 1000)
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const min = 20
      const max = 80
      const step = 0.1
      let w = viewPointW
      if (w <= min) {
        setViewPointWDir(false)
        w = min + step
      } else if (w >= max) {
        setViewPointWDir(true)
        w = max - step
      } else if (viewPointWDir) w -= step
      else w += step
      setViewPointW(w)
    }, 20)

    return () => clearInterval(intervalId)
  }, [viewPointW, viewPointWDir])

  /** Every slide is the same treatment; only the photo changes. */
  const getSlide = (src: StaticImageData, alt: string) => (
    <Image
      alt={alt}
      src={src}
      width={screenDimensions.width}
      height={screenDimensions.height}
      style={{
        boxShadow: !mobileMode
          ? 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px'
          : undefined,
        filter: `brightness(${brightness.toFixed(0)}%)`,
        objectFit: 'cover',
        objectPosition: `${viewPointW}% ${viewPointW}%`,
        transition: 'height 300ms'
      }}
      priority
    />
  )

  const h2Text = (slide: number, text: string) => {
    if (currentSlide !== slide) return null
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: slideDelay / 1000, duration: 0.3 }}
      >
        <Typography
          variant="h2"
          fontSize={getFontH2()}
          sx={{
            textAlign: !mobileMode ? 'left' : 'center',
            lineHeight: 1.5,
            fontWeight: 500,
            color: '#fff',
            textShadow: '0 1px 12px rgba(0,0,0,0.45)'
          }}
        >
          {text}
        </Typography>
      </motion.div>
    )
  }

  const getH2s = () => (
    <Box sx={{ height: '15vh' }}>
      <>
        {h2Text(0, t('hero.slides.1'))}
        {h2Text(1, t('hero.slides.2'))}
        {h2Text(2, t('hero.slides.3'))}
      </>
    </Box>
  )

  // *** SECTION ***
  const getSection = () => (
    <Box
      sx={{
        ...classes.subsection,
        justifyContent: !mobileMode ? 'left' : 'center'
      }}
    >
      {/* Fills the hero exactly; anything wider or taller is clipped. */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <PhotoCarrousel
          ref={Ref}
          setCurrentSlideNo={(ndx: number) => setCurrentSlide(ndx)}
          slides={[
            getSlide(SlideSecurity, 'industrial production floor'),
            getSlide(
              SlideConnectivity,
              'industrial control and network systems'
            ),
            getSlide(SlideMonitoring, 'connected air quality monitoring device')
          ]}
        />
      </Box>

      {/* Darkens the photo enough for the copy to hold at any slide. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: !mobileMode
            ? 'linear-gradient(to right, rgba(9,10,35,0.72) 0%, rgba(9,10,35,0.55) 45%, rgba(9,10,35,0.15) 100%)'
            : 'linear-gradient(to bottom, rgba(9,10,35,0.65) 0%, rgba(9,10,35,0.6) 100%)',
          pointerEvents: 'none',
          zIndex: 5
        }}
      />

      <Box
        sx={{
          ...classes.subsectionDiv,
          padding: getPadding(),
          justifyContent: !mobileMode ? 'left' : 'center',
          marginTop:
            screenDimensions.width <= breakpoints.desktopToMobile
              ? '10rem'
              : '12rem',
          width: screenDimensions.width,
          maxWidth: '1000px'
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Typography
            variant="h1"
            fontSize={getFontH1()}
            sx={{
              marginBottom: '1.25rem',
              textAlign: !mobileMode ? 'left' : 'center',
              lineHeight: !mobileMode ? 1.2 : 1.4,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: '#fff',
              textShadow: '0 2px 16px rgba(0,0,0,0.5)'
            }}
          >
            {t('hero.title')}
          </Typography>
        </motion.div>

        {getH2s()}

        <Box
          style={classes.linkDiv}
          sx={{
            justifyContent: mobileMode ? 'center' : 'left',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.4 }}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}
          >
            <Link href={translateHref(currentLocale, 'services')}>
              <Button variant="contained" sx={classes.linkButton}>
                {t('hero.ctaPrimary')}
              </Button>
            </Link>

            <Link href={translateHref(currentLocale, 'contact')}>
              <Button variant="outlined" sx={classes.linkButtonGhost}>
                {t('hero.ctaSecondary')}
              </Button>
            </Link>
          </motion.div>
        </Box>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        // Keeps the photo layer inside the hero instead of bleeding into the
        // section below it.
        overflow: 'hidden',
        height: screenDimensions.height,
        background: themeColors.kroemtech.dunkelblau
      }}
    >
      {getSection()}
    </Box>
  )
}
